// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useEffect } from "react"
import { Trans } from "@lingui/react"
import * as Sentry from "@sentry/react"
import styled, { ThemeProvider } from "styled-components/macro"
import { GlobalStyles, theme } from "styled/styles"
import { BrowserRouter } from "react-router-dom"
import { Helmet } from "react-helmet"
import Routes from "app/Routes"
import SnackBar from "./features/snackbar/SnackBar"
import { BASE_PATH } from "./app/routing/paths"
import { useDispatch, useSelector } from "react-redux"
import * as UserContextActions from "./features/user-context/actions"
import * as UserContextSlice from "./features/user-context/slice"
import SvgSprite from "./resources/images/svg-sprite.svg?react"
import LoadingIconWrapper from "./features/shared/LoadingIconWrapper"
import * as TimelineActions from "./features/timeline/actions"
import { isIosWiseTime } from "./app/config"
import { sanitizeLocalStorage } from "./lib/localStorage"
import { handleResizeObserverError } from "./lib/resizeObserverErrorHandler"
import {
  SIDEBAR_STORAGE_KEY,
  staleLocalStorageKeys,
} from "./app/localStorageKeys"
import { SIDEBAR_COLLAPSED_CLASSNAME } from "./features/shared/classNames"

const Main = styled.main`
  display: flex;
  flex-direction: column;
`

window.addEventListener("error", handleResizeObserverError)

const handleAppcues = (userId: string, props: { email: string, name: string}) => {
  if (!window.loadjs) {
    return
  }
  window.loadjs.ready("Appcues", () => {
    if (!window.Appcues) {
      return
    }
    window.Appcues.start()
    window.Appcues.identify(userId, props)
  })
}

const App = (): React.ReactElement<unknown> => {

  /**
   * Ensure the user is logged in by:
   *
   * 1. Calling the `whoAmI` endpoint to check if the user has a valid id token
   * 2. The accompanying retry policy will attempt to exchange a _refresh token for an _id token if the previous call
   *    fails; if the token exchange fails then the user is redirected to the login page.
   */
  const dispatch = useDispatch()
  useEffect(() => {
    // handle collapsed state of wt-sidebar to avoid content jump
    const { body }: Document = document
    if (body !== null && localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true") {
      body.classList.add(SIDEBAR_COLLAPSED_CLASSNAME)
    }
    dispatch(UserContextActions.fetchUserDetails())
    dispatch(TimelineActions.syncCurrentDay())
    dispatch(TimelineActions.periodicRefreshTimesheetFieldConfigs())

    document.body.addEventListener("mousedown", function() {
      document.body.classList.add("focus-using-mouse")
    })

    document.body.addEventListener("keydown", function(event) {
      if (event.key === "Tab") {
        document.body.classList.remove("focus-using-mouse")
      }
    })
    sanitizeLocalStorage(staleLocalStorageKeys)
  }, [])

  const user = useSelector(UserContextSlice.selectUser)
  const { id, email, firstName, lastName } = user || {}
  useEffect(() => {
    if (id && email && firstName && lastName) {
      handleAppcues(id, { email, name: firstName + " " + lastName })
      Sentry.setUser({ id })
    }
  }, [id, email, firstName, lastName])
  useEffect(() => {
    if (id !== undefined) {
      dispatch(UserContextActions.listNotifications())
    }
  }, [id])
  const maximumScale = isIosWiseTime ? ", maximum-scale=1" : ""

  return (
    <Sentry.ErrorBoundary
      fallback={<p><Trans id="label_an_error_has_occurred" message="An error has occurred"/></p>}
    >
      <ThemeProvider theme={theme}>
        <Helmet>
          <meta name="viewport" content={`width=device-width, initial-scale=1${maximumScale}`}/>
        </Helmet>
        <GlobalStyles/>
        <SvgSprite className="hidden"/>
        <Main>
          <BrowserRouter
            basename={BASE_PATH}
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <React.Suspense fallback={<LoadingIconWrapper/>}>
              <Routes/>
            </React.Suspense>
          </BrowserRouter>
          <SnackBar/>
        </Main>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}

export default App
